import React, { useState } from 'react';
import './FAQ.css';

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState('general');
  const [openQuestions, setOpenQuestions] = useState([]);

  const toggleQuestion = (id) => {
    setOpenQuestions(prev => 
      prev.includes(id) 
        ? prev.filter(q => q !== id)
        : [...prev, id]
    );
  };

  const scrollToWaitlist = (e) => {
    e.preventDefault();
    const waitlistSection = document.getElementById('waitlist-section');
    if (waitlistSection) {
      const waitlistPosition = waitlistSection.getBoundingClientRect().top + window.pageYOffset;
      const offset = 30; // Same offset as in the NavBar component
      window.scrollTo({
        top: waitlistPosition - offset,
        behavior: 'smooth'
      });
    }
  };

  const categories = {
    general: 'General Questions',
    technical: 'Technical Details',
    privacy: 'Privacy & Security',
    pricing: 'Pricing & Access'
  };

  const faqs = {
    general: [
      {
        id: 'how-works',
        question: 'How does the AI assistant understand my needs?',
        answer: 'Our AI uses advanced natural language processing to understand your unique thinking style and preferences. It learns from your interactions to provide increasingly personalized support, adapting its communication and planning strategies to match your cognitive style.'
      },
      {
        id: 'who-for',
        question: 'Who can benefit from using Attenxion?',
        answer: "Attenxion is for anyone who wants to finish projects with more clarity and less friction—from individuals to enterprise teams. It adapts to different working styles and is especially helpful for those underserved by traditional tools, including many neurodivergent professionals."
      }
    ],
    technical: [
      {
        id: 'calendar-sync',
        question: 'How does calendar integration work?',
        answer: 'We seamlessly sync with major calendar platforms (Google Calendar, Apple Calendar, Outlook) while respecting your existing commitments. The AI analyzes your schedule patterns and energy levels to suggest optimal time blocks for different types of tasks.'
      },
      {
        id: 'offline-access',
        question: 'Can I use the tool offline?',
        answer: "While core features require an internet connection for AI processing, we offer offline access to your existing plans and materials. Changes sync automatically when you're back online."
      }
    ],
    privacy: [
      {
        id: 'data-handling',
        question: 'How is my data protected?',
        answer: 'We use enterprise-grade encryption for all data storage and transmission. Your personal information and project details are never shared with third parties. You can export or delete your data at any time.'
      },
      {
        id: 'ai-privacy',
        question: 'Does the AI store my conversations?',
        answer: 'While the AI learns from your interaction patterns to provide better support, actual conversation content is not permanently stored. You can clear your interaction history at any time.'
      }
    ],
    pricing: [
      {
        id: 'pricing-model',
        question: 'What are the pricing options?',
        answer: "During our beta phase, early adopters receive special founding member pricing. We offer different pricing tiers for individuals, teams, and enterprises, based on size and needs."
      },
      {
        id: 'trial',
        question: 'Is there a free trial?',
        answer: 'Yes! We offer a 5 day full-feature trial so you can experience how our tool adapts to your needs. No credit card required to start.'
      }
    ]
  };

  return (
    <section className="faq" id="faq">
      <div className="faq-container">
        <h2>Frequently Asked Questions</h2>
        <p className="faq-intro">
          Find answers to common questions about our AI assistant and how it can help you.
        </p>

        <div className="category-tabs">
          {Object.entries(categories).map(([key, label]) => (
            <button
              key={key}
              className={`category-tab ${activeCategory === key ? 'active' : ''}`}
              onClick={() => setActiveCategory(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="questions-container">
          {faqs[activeCategory].map(({ id, question, answer }, index) => (
            <div 
              key={id}
              className={`question-card ${openQuestions.includes(id) ? 'open' : ''}`}
              data-aos="fade-up"
              data-aos-delay={index * 50}
            >
              <button
                className="question-header"
                onClick={() => toggleQuestion(id)}
                aria-expanded={openQuestions.includes(id)}
              >
                <span className="question-text">{question}</span>
                <span className="toggle-icon">
                  {openQuestions.includes(id) ? '−' : '+'}
                </span>
              </button>
              <div className="answer-container">
                <p className="answer-text">{answer}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="more-questions">
          <h3>Still have questions?</h3>
          <p>We're here to help! Reach out to our team.</p>
          <a 
            href="#waitlist-section" 
            className="contact-button" 
            onClick={scrollToWaitlist}
          >
            Contact Us
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQ; 